// src/pages/Login.js
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import { loginUser, resetPassword } from '../firebase/services';

export default function Login() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [mode, setMode] = useState('login'); // login | reset
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await loginUser(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(err.message.replace('Firebase: ', '').replace(/\(auth.*\)/, ''));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await resetPassword(email);
      setResetSent(true);
    } catch (err) {
      setError(err.message.replace('Firebase: ', '').replace(/\(auth.*\)/, ''));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={s.page}>
      <div style={s.card}>
        <Link to="/" style={s.logo}>COA<span style={s.logoAccent}>Lab</span></Link>

        {mode === 'login' ? (
          <>
            <h1 style={s.title}>{t('auth.login.title')}</h1>
            <p style={s.sub}>{t('auth.login.subtitle')}</p>
            {error && <div style={s.error}>{error}</div>}
            <form onSubmit={handleLogin} style={s.form}>
              <label style={s.label}>{t('auth.login.email')}</label>
              <input style={s.input} type="email" value={email} onChange={e => setEmail(e.target.value)} required autoComplete="email" />
              <label style={s.label}>{t('auth.login.password')}</label>
              <input style={s.input} type="password" value={password} onChange={e => setPassword(e.target.value)} required autoComplete="current-password" />
              <button type="button" style={s.forgotBtn} onClick={() => { setMode('reset'); setError(''); }}>
                {t('auth.login.forgot')}
              </button>
              <button type="submit" style={s.submit} disabled={loading}>
                {loading ? t('common.loading') : t('auth.login.submit')}
              </button>
            </form>
            <p style={s.switchText}>
              {t('auth.login.noAccount')}{' '}
              <Link to="/register" style={s.switchLink}>{t('auth.login.create')}</Link>
            </p>
          </>
        ) : (
          <>
            <h1 style={s.title}>{t('auth.reset.title')}</h1>
            <p style={s.sub}>{t('auth.reset.subtitle')}</p>
            {resetSent && <div style={s.success}>{t('auth.reset.sent')}</div>}
            {error && <div style={s.error}>{error}</div>}
            <form onSubmit={handleReset} style={s.form}>
              <label style={s.label}>{t('auth.reset.email')}</label>
              <input style={s.input} type="email" value={email} onChange={e => setEmail(e.target.value)} required />
              <button type="submit" style={s.submit} disabled={loading}>
                {loading ? t('common.loading') : t('auth.reset.submit')}
              </button>
            </form>
            <button onClick={() => { setMode('login'); setError(''); setResetSent(false); }} style={s.switchLink}>{t('auth.reset.back')}</button>
          </>
        )}
      </div>
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: '#070F2B', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, fontFamily: "'Inter', sans-serif" },
  card: { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 20, padding: '40px 36px', width: '100%', maxWidth: 420 },
  logo: { display: 'block', fontSize: 22, fontWeight: 800, color: '#fff', textDecoration: 'none', marginBottom: 28 },
  logoAccent: { color: '#4A9EFF' },
  title: { fontSize: 26, fontWeight: 800, color: '#fff', margin: '0 0 6px' },
  sub: { fontSize: 14, color: 'rgba(255,255,255,0.55)', marginBottom: 28 },
  error: { background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, padding: '10px 14px', color: '#FCA5A5', fontSize: 13, marginBottom: 16 },
  success: { background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.3)', borderRadius: 10, padding: '10px 14px', color: '#6EE7B7', fontSize: 13, marginBottom: 16 },
  form: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.7)', marginBottom: 4 },
  input: { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, padding: '12px 14px', color: '#fff', fontSize: 14, outline: 'none', marginBottom: 12 },
  forgotBtn: { background: 'none', border: 'none', color: '#4A9EFF', fontSize: 13, cursor: 'pointer', textAlign: 'right', alignSelf: 'flex-end', padding: 0, marginBottom: 8 },
  submit: { background: 'linear-gradient(135deg, #1E5EFF, #0A3DD4)', color: '#fff', border: 'none', borderRadius: 12, padding: '13px', fontSize: 15, fontWeight: 700, cursor: 'pointer', marginTop: 4 },
  switchText: { textAlign: 'center', fontSize: 14, color: 'rgba(255,255,255,0.5)', marginTop: 20 },
  switchLink: { color: '#4A9EFF', textDecoration: 'none', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', fontSize: 14 },
};
